import { useBandContext, useCalendarContext, useEventContext } from "Bands/contexts";
import { dispatchGetEvents, eventBlockSelectorCreator } from "Bands/state";
import { dispatchCopyEventBlocks } from "Bands/state/data/actions/eventBlocks/copyEventBlock";
import { EventBlocksType, EventUniqueBlocks, IEventBlock, IEventPeopleBlock } from "Bands/types";
import { getEventBlockType } from "Bands/utils";
import { FC, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { copy } from "shared/assets/copy";
import { File } from "shared/assets/icons";
import { Modal } from "shared/components";
import { addTemporaryToast } from "shared/components/Toast/state";
import { API_ENDPOINTS } from "shared/constants";
import { IStatus } from "shared/types";
import { StringParam, useQueryParams } from "use-query-params";
import { PushCopyBlockForm } from "./PushCopyBlockForm";
import { PushCopyStep2 } from "./steps/PushCopyStep2";
import { PushCopyStep3 } from "./steps/PushCopyStep3";

interface Props {
  showPushCopyModal: IEventBlock;
  setShowPushCopyModal: (showPushCopyModal: IEventBlock | null) => void;
}

export const renderCopy = (c: string, blockType: EventBlocksType) => {
  return c.replaceAll(
    "%blockTypeCopy",
    copy.copyBlock.blockTypeCopies[
      blockType as keyof typeof copy.copyBlock.blockTypeCopies
    ].toLowerCase()
  );
};

export const PushCopyModal: FC<Props> = ({ showPushCopyModal, setShowPushCopyModal }) => {
  const dispatch = useDispatch();
  const [step, setStep] = useState<1 | 2 | 3>(
    showPushCopyModal.type === EventUniqueBlocks.people ? 1 : 2
  );
  const { event } = useEventContext();
  const { bandId } = useBandContext();
  const { allEvents, start, stop, refetchEvents } = useCalendarContext();
  const [, setQuery] = useQueryParams({
    month: StringParam,
    year: StringParam,
    view: StringParam,
  });

  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [completedRequests, setCompletedRequests] = useState<string[]>();
  const [failedRequests, setFailedRequests] = useState<string[]>();
  const [isLoading, setIsLoading] = useState(false);
  const [includeRsvpUsers, setIncludeRsvpUsers] = useState(true);

  const handleModalClose = useCallback(() => {
    setQuery({
      month: undefined,
      year: undefined,
      view: undefined,
    });

    setShowPushCopyModal(null);
    setSelectedEvents([]);
    setStep(1);
  }, [setQuery, setShowPushCopyModal, setStep]);

  const handleCheckboxChange = (value: string) => {
    setSelectedEvents(prev => {
      if (prev.includes(value)) {
        return prev.filter(id => id !== value);
      }
      return [...prev, value];
    });
  };

  const onFinalSubmit = useCallback(
    ({ newEvents }) => {
      setCompletedRequests([]);
      setFailedRequests([]);

      dispatchCopyEventBlocks(
        dispatch,
        newEvents ||
          allEvents.filter(e => selectedEvents.includes(e.id)).map(e => API_ENDPOINTS.EVENT(e.id)),
        [getEventBlockType(showPushCopyModal.type)?.api.item(showPushCopyModal.id)!],
        includeRsvpUsers,
        setCompletedRequests,
        setFailedRequests,
        setIsLoading
      );
      refetchEvents();
    },
    [
      allEvents,
      dispatch,
      selectedEvents,
      showPushCopyModal.id,
      showPushCopyModal.type,
      includeRsvpUsers,
      refetchEvents,
    ]
  );

  useEffect(() => {
    dispatchGetEvents(dispatch, bandId ?? undefined, start, stop);
  }, [bandId, dispatch, start, stop]);

  const peopleBlockSelector = eventBlockSelectorCreator(
    showPushCopyModal.id,
    EventUniqueBlocks.people
  );

  const peopleBlock = useSelector(peopleBlockSelector) as IEventPeopleBlock;

  useEffect(() => {
    if (completedRequests && completedRequests.length === selectedEvents.length) {
      handleModalClose();
      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: renderCopy(copy.pushCopyBlock.step1.success, showPushCopyModal.type),
        })
      );
    }
  }, [
    completedRequests,
    selectedEvents.length,
    handleModalClose,
    dispatch,
    showPushCopyModal.type,
  ]);

  return (
    <Modal
      title={"Copy to other event(s)"}
      icon={File}
      handleClose={handleModalClose}
      showCloseButton
    >
      {step === 1 && (
        <PushCopyBlockForm
          sourceEvent={event}
          sourceBlock={peopleBlock}
          blockType={showPushCopyModal?.type}
          setStep={setStep}
          handleModalClose={handleModalClose}
          setIncludeRsvpUsers={setIncludeRsvpUsers}
        />
      )}
      {step === 2 && (
        <PushCopyStep2
          selectedEvents={selectedEvents}
          setStep={setStep}
          handleModalClose={handleModalClose}
          handleCheckboxChange={handleCheckboxChange}
          setSelectedEvents={setSelectedEvents}
        />
      )}
      {step === 3 && (
        <PushCopyStep3
          showPushCopyModal={showPushCopyModal}
          allEvents={allEvents}
          selectedEvents={selectedEvents}
          isLoading={isLoading}
          completedRequests={completedRequests || []}
          failedRequests={failedRequests || []}
          setStep={setStep}
          onFinalSubmit={onFinalSubmit}
          setFailedRequests={setFailedRequests}
        />
      )}
    </Modal>
  );
};
